import React, { useState, useEffect, createContext, useContext, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import './styles/animations.css';
import { trackPageView, trackLogin, trackSignup } from './utils/analytics';

// Contexts
import { PWAProvider } from './context/PWAContext';
import { FeatureFlagProvider } from './context/FeatureFlagContext';
import { ThemeProvider } from './context/ThemeContext';

// Critical components - loaded immediately
import Login from './components/Login';
import Register from './components/Register';
import RegisterBasic from './components/RegisterBasic';
import LandingPage from './components/LandingPage';
import PublicPage from './components/PublicPage';
import ErrorBoundary from './components/ErrorBoundary';
import PWAGuard from './components/PWAGuard';
import GoogleAuthCallback from './components/GoogleAuthCallback';
import RoadOpsAuthCallback from './components/RoadOpsAuthCallback';
import AppleAuthCallback from './components/AppleAuthCallback';

// Non-lazy components (imports must come before lazy() consts to satisfy
// `import/first`). PWAManagerSimple removed — PWAGuard/PWAContext handles this.
import OnboardingModals from './components/OnboardingModals';
import LegalAcknowledgmentModal from './components/LegalAcknowledgmentModal';
import LocationPermissionModal from './components/LocationPermissionModal';
import FirstLoginTutorialOverlay from './components/FirstLoginTutorialOverlay';
import { PermissionsProvider } from './components/FeatureGate';

// Loading Spinner Component
const LoadingSpinner = () => (
  <div className="min-h-screen bg-gray-900 flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-orange-500 mx-auto mb-4"></div>
      <p className="text-gray-400 text-lg">Loading...</p>
    </div>
  </div>
);

const AdvertiserPortal = lazy(() => import('./pages/AdvertiserPortal'));
const TermsOfUse = lazy(() => import('./pages/TermsOfUse'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));

// Lazy-loaded components - split into separate chunks
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const CompanyDashboard = lazy(() => import('./components/CompanyDashboard'));
// DriverDashboard removed - /driver/* now redirects to /dashboard (DriverSocialDashboard)
const DriverSocialDashboard = lazy(() => import('./components/social/DriverSocialDashboard'));
const DriverPublicProfile = lazy(() => import('./pages/DriverPublicProfile'));
const GuestDashboard = lazy(() => import('./components/GuestDashboard'));
const UserProfile = lazy(() => import('./components/UserProfile'));
const UserProfileNew = lazy(() => import('./components/UserProfileNew'));
const SocialProfile = lazy(() => import('./components/SocialProfile'));
const CompanyProfile = lazy(() => import('./components/CompanyProfile'));
const ProfileRouter = lazy(() => import('./components/ProfileRouter'));
const ProfileCompletion = lazy(() => import('./components/ProfileCompletion'));
const EditTourProfilePage = lazy(() => import('./components/EditTourProfilePage'));
const ForgotPassword = lazy(() => import('./components/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));
const ResetPasswordEmail = lazy(() => import('./components/ResetPasswordEmail'));
const HelpGuide = lazy(() => import('./components/HelpGuide'));
const Chatbot = lazy(() => import('./components/ChatbotNew'));
const SmartRecruiterDashboard = lazy(() => import('./components/SmartRecruiterDashboard'));
const MobileDriverView = lazy(() => import('./components/MobileDriverView'));
const TourSetup = lazy(() => import('./components/TourSetup'));
const TourSetupEnhanced = lazy(() => import('./components/TourSetupEnhanced'));
const NetworkManager = lazy(() => import('./components/NetworkManager'));
const MembershipPage = lazy(() => import('./components/MembershipPage'));
const CompliancePage = lazy(() => import('./pages/CompliancePage'));
const ForDriversPage = lazy(() => import('./pages/ForDriversPage'));
const ForCompaniesPage = lazy(() => import('./pages/ForCompaniesPage'));
const ForToursPage = lazy(() => import('./pages/ForToursPage'));
const MarketingCompanies = lazy(() => import('./components/marketing/MarketingCompanies'));
const MarketingDrivers = lazy(() => import('./components/marketing/MarketingDrivers'));
const PublicGigPage = lazy(() => import('./components/PublicGigPage'));
const MarketingOrganizers = lazy(() => import('./components/marketing/MarketingOrganizers'));
const PublicUpdates = lazy(() => import('./components/PublicUpdates'));
const IdeasBoard = lazy(() => import('./components/IdeasBoard'));
// ContractsPage removed per user request (E-Signatures feature not needed)

// Feature Preview Pages - lazy loaded
const HelpGuideFeaturePreview = lazy(() => import('./components/HelpGuideFeaturePreview'));
const AIChatbotFeaturePreview = lazy(() => import('./components/AIChatbotFeaturePreview'));
const TheGreenRoomPreview = lazy(() => import('./components/TheGreenRoomPreview'));
const GreenRoomFull = lazy(() => import('./components/GreenRoom'));
const VenuesFeaturePreview = lazy(() => import('./components/VenuesFeaturePreview'));
const VenueWikiPage = lazy(() => import('./components/VenueWikiPage'));
const ResourceWikiPage = lazy(() => import('./components/ResourceWikiPage'));
const AirportFeaturePreview = lazy(() => import('./components/AirportFeaturePreview'));
const MasterTourFeaturePreview = lazy(() => import('./components/MasterTourFeaturePreview'));
const AnalyticsDashboard = lazy(() => import('./components/AnalyticsDashboard'));
const AnalyticsFeaturePreview = lazy(() => import('./components/AnalyticsFeaturePreview'));
const MobileAppFeaturePreview = lazy(() => import('./components/MobileAppFeaturePreview'));

// Dynamic backend URL - uses environment variable or falls back to current origin for production
// Normalize URL to handle www vs non-www consistently
const normalizeUrl = (url) => {
  if (!url) return url;
  // For hopseat.app, always use non-www version to avoid CORS issues
  return url.replace('https://www.hopseat.app', 'https://hopseat.app');
};

const getBackendUrl = () => {
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  const winOrigin = (typeof window !== 'undefined' && window.location?.origin) || '';

  // In production (Emergent deployment), frontend + backend share the same origin
  // served by Kubernetes ingress. If REACT_APP_BACKEND_URL was baked into the
  // bundle at a DIFFERENT Emergent domain (e.g. a stale preview URL from an
  // older build), using that env value would send API calls cross-origin and
  // set auth cookies on the wrong domain — breaking login entirely. Prefer
  // same-origin whenever the page is running on a real hostname.
  if (winOrigin && !winOrigin.includes('localhost')) {
    return normalizeUrl(winOrigin);
  }

  if (envUrl && envUrl.trim() !== '') {
    return normalizeUrl(envUrl);
  }

  return 'http://localhost:8001';
};

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

// NOTE: `axios.defaults.withCredentials = true` was temporarily enabled to
// migrate auth to httpOnly cookies, but on production it caused a class of
// CORS/credential conflicts that could block login when the backend origin
// drifts. Auth still works fine via the `Authorization: Bearer` header that
// every page already sets from localStorage — the backend accepts either.
// Credentialed cookie mode will be re-enabled once we verify the prod CORS
// secret is an explicit origin list (not `*`).
// axios.defaults.withCredentials = true;

// Log the configured backend URL for debugging
console.log('🔧 Backend URL configured:', BACKEND_URL);

// Auth Context
const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const keepLoggedIn = localStorage.getItem('keepLoggedIn') === 'true';
    const loginExpiry = localStorage.getItem('loginExpiry');
    
    if (token) {
      // Check if persistent login has expired
      if (keepLoggedIn && loginExpiry) {
        const expiryTime = parseInt(loginExpiry);
        if (Date.now() > expiryTime) {
          // Persistent login expired, clear everything
          logout();
          setLoading(false);
          return;
        }
      }
      
      // Token is valid, restore authentication
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch current user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  // Listen for profile photo / DQ updates from anywhere in the app and refetch
  // the user so every header/sidebar/avatar refreshes in real time.
  useEffect(() => {
    const handler = () => {
      const tok = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!tok) return;
      axios.get(`${API}/auth/me`)
        .then((r) => setUser(r.data))
        .catch(() => { /* silent — token may have just expired */ });
    };
    window.addEventListener('hopseat:profile-photo-updated', handler);
    window.addEventListener('hopseat:user-updated', handler);
    return () => {
      window.removeEventListener('hopseat:profile-photo-updated', handler);
      window.removeEventListener('hopseat:user-updated', handler);
    };
  }, []);

  // Register with OneSignal (CDN) — check for existing subscription on login
  // NOTE: push permission must be requested via user interaction (see NotificationBell component)
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (!token) return;

    const registerPlayerId = (playerId) => {
      if (!playerId) return;
      const cacheKey = `onesignal_registered_${user.id}`;
      if (sessionStorage.getItem(cacheKey) === playerId) return;
      axios.put(`${API}/social/onesignal-player-id`, { player_id: playerId }, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(() => {
        sessionStorage.setItem(cacheKey, playerId);
      }).catch(() => {});
    };

    const waitForSubscriptionId = (timeoutMs = 12000, intervalMs = 600) => {
      return new Promise((resolve) => {
        const start = Date.now();
        const probe = () => {
          const OS = window.OneSignal;
          const id = OS?.User?.PushSubscription?.id;
          if (id) {
            resolve(id);
            return;
          }
          if (Date.now() - start > timeoutMs) {
            resolve(null);
            return;
          }
          setTimeout(probe, intervalMs);
        };
        probe();
      });
    };

    const checkAndRegister = async () => {
      const OS = window.OneSignal;
      if (!OS) return;
      try {
        const existingId = OS.User?.PushSubscription?.id;
        if (existingId) {
          registerPlayerId(existingId);
        } else {
          const delayedId = await waitForSubscriptionId();
          if (delayedId) registerPlayerId(delayedId);
        }

        OS.User?.PushSubscription?.addEventListener?.('change', (event) => {
          registerPlayerId(event.current?.id || OS.User?.PushSubscription?.id);
        });
      } catch (err) {
        console.warn('[OneSignal] registration check error:', err?.message);
      }
    };

    if (window.OneSignal?.User) {
      checkAndRegister();
    } else {
      window.OneSignalDeferred = window.OneSignalDeferred || [];
      window.OneSignalDeferred.push(() => setTimeout(checkAndRegister, 500));
    }
  }, [user?.id]);

  const login = async (email, password, keepLoggedIn = false) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      
      // Clear any stale impersonation data before setting new session
      localStorage.removeItem('admin_token');
      localStorage.removeItem('impersonation_data');

      if (keepLoggedIn) {
        localStorage.setItem('token', access_token);
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('keepLoggedIn', 'true');
        localStorage.setItem('loginExpiry', (Date.now() + 30 * 24 * 60 * 60 * 1000).toString());
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
      } else {
        sessionStorage.setItem('token', access_token);
        sessionStorage.setItem('user', JSON.stringify(userData));
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('keepLoggedIn');
        localStorage.removeItem('loginExpiry');
      }
      
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(userData);
      trackLogin(userData.role);
      return userData;
    } catch (error) {
      console.error('Login error:', error);
      console.error('Login error details:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      // Surface an ACTIONABLE message instead of the cryptic "Login failed".
      // The fallback used to fire for every kind of failure, including
      // network/CORS issues where the server never returned a body — leaving
      // users (and support) with zero diagnostic signal.
      const detail = error.response?.data?.detail;
      if (detail) {
        // Server responded with a real reason (wrong password, locked, etc).
        throw new Error(detail);
      }
      if (error.response?.status >= 500) {
        throw new Error('The server is having a problem right now. Please try again in a minute.');
      }
      // No response object at all → network / CORS / PWA cache issue.
      // Most commonly seen on iOS PWAs running stale cached JS after a deploy.
      throw new Error(
        "Couldn't reach the sign-in service. If you're using the HopSeat app on your phone, force-close it and reopen, or try signing in from a browser tab. (network error)"
      );
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${API}/auth/register`, userData);
      trackSignup(userData.role);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Registration failed');
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('keepLoggedIn');
    localStorage.removeItem('loginExpiry');
    localStorage.removeItem('admin_token');
    localStorage.removeItem('impersonation_data');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    // Clear persisted dashboard tab state so the next signed-in user starts fresh
    sessionStorage.removeItem('hopseat:driverDashboard:activeTab');
    sessionStorage.removeItem('hopseat:driverDashboard:resourceTab');
    sessionStorage.removeItem('hopseat:companyDashboard:activeTab');
    sessionStorage.removeItem('adminActiveTab');
    delete axios.defaults.headers.common['Authorization'];
    // Clear httpOnly cookie via backend
    axios.post(`${BACKEND_URL}/api/auth/logout`).catch(() => {});
    setUser(null);
  };

  const value = {
    user,
    login,
    register,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  // Check if profile is complete (except for master admin and advertisers who use their own portal)
  // Wave 3D: drivers AND companies reach Home first — profile/DQ/company-setup is
  // surfaced as the Home next-best-action instead of a pre-dashboard wall. Security
  // setup is the top Home task (single source of truth). Marketplace eligibility
  // (drivers applying to gigs) remains enforced by existing backend rules.
  // Tour organizers intentionally keep their existing flow (kept stable this wave).
  if (
    user.role !== 'master_admin' &&
    user.role !== 'advertiser' &&
    user.role !== 'driver' &&
    user.role !== 'bus_company' &&
    !user.profile_complete
  ) {
    return <Navigate to="/complete-profile" />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    return <Navigate to="/unauthorized" />;
  }

  return children;
};

// Impersonation Exit Button — replaces the old top banner
const ExitPreviewButton = () => {
  const [hovered, setHovered] = useState(false);
  const [data, setData] = useState(() => {
    try { return JSON.parse(localStorage.getItem('impersonation_data')); } catch { return null; }
  });

  useEffect(() => {
    const check = () => {
      try { setData(JSON.parse(localStorage.getItem('impersonation_data')) || null); } catch { setData(null); }
    };
    const interval = setInterval(check, 1000);
    window.addEventListener('storage', check);
    return () => { clearInterval(interval); window.removeEventListener('storage', check); };
  }, []);

  if (!data) return null;

  const handleExit = () => {
    const adminToken = localStorage.getItem('admin_token');
    if (adminToken) {
      localStorage.setItem('token', adminToken);
      localStorage.removeItem('admin_token');
      localStorage.removeItem('impersonation_data');
      localStorage.removeItem('user'); // Clear stale impersonated user data
      window.location.href = '/dashboard';
    }
  };

  return (
    <>
      {/* Exit preview button — top-right on mobile, bottom-right on desktop */}
      <button
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={handleExit}
        title="Return to Admin"
        className="exit-preview-btn"
        style={{
          position: 'fixed',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          gap: hovered ? '8px' : '0',
          padding: hovered ? '7px 14px' : '9px',
          background: hovered ? '#ea580c' : 'rgba(15,23,42,0.95)',
          border: '1.5px solid rgba(234,88,12,0.5)',
          borderRadius: '9999px',
          color: hovered ? '#fff' : '#fb923c',
          cursor: 'pointer',
          boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
          backdropFilter: 'blur(8px)',
          transition: 'all 0.2s ease',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
        }}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
          <polyline points="16 17 21 12 16 7"/>
          <line x1="21" y1="12" x2="9" y2="12"/>
        </svg>
        {hovered && <span style={{ fontSize: '13px', fontWeight: 600 }}>Return to Admin</span>}
      </button>
    </>
  );
};

// Analytics Wrapper Component
const AnalyticsWrapper = () => {
  const { user } = useAuth();
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  return <AnalyticsDashboard token={token} role={user?.role} />;
};

// Help Guide Wrapper Component
const HelpGuideWrapper = () => {
  const { user } = useAuth();
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  
  return (
    <HelpGuide 
      token={token}
      userRole={user?.role}
    />
  );
};

// ── Google Analytics 4 route tracker ─────────────────────────────────────────
// Must live inside <BrowserRouter> to use useLocation.
const GATracker = () => {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);
  return null;
};

// Dashboard Router Component
// The Green Room — marketplace-style listing page, accessible to all authenticated users
const GreenRoomPage = () => {
  const { user } = useAuth();
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  return (
    <Suspense fallback={<div className="flex items-center justify-center h-screen" style={{background:'var(--app-bg)'}}><div className="animate-spin w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full" /></div>}>
      <GreenRoomFull token={token} userRole={user?.role} currentUserId={user?.id} />
    </Suspense>
  );
};

const DashboardRouter = () => {
  const { user, logout } = useAuth();

  if (!user) return <Navigate to="/login" />;

  const renderDashboardByRole = () => {
    switch (user.role) {
      case 'master_admin':
      case 'admin':
        return (
          <ErrorBoundary>
            <AdminDashboard />
          </ErrorBoundary>
        );
      case 'bus_company':
        return (
          <ErrorBoundary>
            <CompanyDashboard />
          </ErrorBoundary>
        );
      case 'driver':
        return (
          <ErrorBoundary>
            <DriverSocialDashboard user={user} onLogout={logout} />
          </ErrorBoundary>
        );
      case 'guest_tour':
        return (
          <ErrorBoundary>
            <GuestDashboard />
          </ErrorBoundary>
        );
      case 'advertiser':
        return <Navigate to="/advertise" replace />;
      default:
        return <Navigate to="/login" />;
    }
  };

  return (
    <>
      <ExitPreviewButton />
      <FirstLoginTutorialOverlay user={user} />
      {renderDashboardByRole()}
    </>
  );
};

// Main App Component
function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
      <PermissionsProvider>
      <FeatureFlagProvider>
        <PWAProvider>
          <PWAAppWrapper>
            <div className="App">
              <BrowserRouter>
                <GATracker />
                <Suspense fallback={<LoadingSpinner />}>
                <Routes>
            {/* Google OAuth Callback — must be before other routes */}
            <Route path="/auth/google/callback" element={<GoogleAuthCallback />} />
            <Route path="/auth/roadops/callback" element={<RoadOpsAuthCallback />} />
            <Route path="/auth/apple/callback" element={<AppleAuthCallback />} />

            <Route path="/" element={<LandingPage />} />
            <Route path="/p/:slug" element={<PublicPage />} />
            <Route path="/driver/:driverId" element={<DriverPublicProfile />} />
            <Route path="/gig/:id" element={<PublicGigPage />} />
            <Route
              path="/venues/:id"
              element={
                <ProtectedRoute>
                  <VenueWikiPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/resources/:rtype/:id"
              element={
                <ProtectedRoute>
                  <ResourceWikiPage />
                </ProtectedRoute>
              }
            />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<RegisterBasic />} />
            <Route path="/compliance" element={<CompliancePage />} />
            <Route path="/for-drivers" element={<ForDriversPage />} />
            <Route path="/for-companies" element={<ForCompaniesPage />} />
            <Route path="/for-tours" element={<ForToursPage />} />
            <Route path="/marketing/companies" element={<MarketingCompanies />} />
            <Route path="/marketing/drivers" element={<MarketingDrivers />} />
            <Route path="/marketing/organizers" element={<MarketingOrganizers />} />
            <Route path="/updates" element={<PublicUpdates />} />
            <Route path="/whats-new" element={<PublicUpdates />} />
            <Route path="/ideas" element={<IdeasBoard />}/>
            <Route path="/register-old" element={<Register />} />
            <Route path="/complete-profile" element={<ProfileCompletion />} />
            
            {/* Password Reset Routes - Public (email-based recovery) */}
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/security-questions" element={<Navigate to="/forgot-password" replace />} />
            <Route path="/reset-password/new-password" element={<ResetPassword />} />
            <Route path="/reset-password/email" element={<ResetPasswordEmail />} />
            
            {/* Security questions removed — deprecated route redirects to Home */}
            <Route path="/security-questions/setup" element={<Navigate to="/dashboard" replace />} />
            
            {/* Public Profile Routes - No authentication required */}
            <Route path="/profile/:username" element={<UserProfileNew />} />
            
            {/* Edit Tour Profile - Protected, tour organizer only */}
            <Route 
              path="/profile/edit-tour" 
              element={
                <ProtectedRoute>
                  <EditTourProfilePage />
                </ProtectedRoute>
              } 
            />
            
            {/* Protected Profile Routes */}
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <DashboardRouter />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/help" 
              element={
                <ProtectedRoute>
                  <HelpGuideWrapper />
                </ProtectedRoute>
              } 
            />
            
            {/* Feature Preview Routes */}
            <Route 
              path="/features/help-guide" 
              element={
                <ProtectedRoute>
                  <HelpGuideFeaturePreview />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/features/ai-chatbot" 
              element={
                <ProtectedRoute>
                  <AIChatbotFeaturePreview />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/features/the-green-room" 
              element={
                <ProtectedRoute>
                  <TheGreenRoomPreview />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/features/venues" 
              element={
                <ProtectedRoute>
                  <VenuesFeaturePreview />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/features/airports" 
              element={
                <ProtectedRoute>
                  <AirportFeaturePreview />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/features/master-tour" 
              element={
                <ProtectedRoute>
                  <MasterTourFeaturePreview />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/features/analytics" 
              element={
                <ProtectedRoute>
                  <AnalyticsWrapper />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/features/mobile-app" 
              element={
                <ProtectedRoute>
                  <MobileAppFeaturePreview />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/user/:userId" 
              element={
                <ProtectedRoute>
                  <ProfileRouter />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/network" 
              element={
                <ProtectedRoute allowedRoles={['driver', 'master_admin']}>
                  <NetworkManager />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/membership" 
              element={<MembershipPage />} 
            />
            {/* E-Signatures/Contracts route removed per user request */}
            <Route 
              path="/profile-old/:userId" 
              element={
                <ProtectedRoute>
                  <UserProfile />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/dashboard/admin/*" 
              element={
                <ProtectedRoute allowedRoles={['admin', 'master_admin']}>
                  <ErrorBoundary>
                    <AdminDashboard />
                  </ErrorBoundary>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/company/*" 
              element={
                <ProtectedRoute allowedRoles={['bus_company']}>
                  <ErrorBoundary>
                    <CompanyDashboard />
                  </ErrorBoundary>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/guest/*" 
              element={
                <ProtectedRoute allowedRoles={['guest_tour']}>
                  <GuestDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/tour-setup" 
              element={
                <ProtectedRoute allowedRoles={['admin', 'guest_tour', 'bus_company']}>
                  <TourSetupEnhanced />
                </ProtectedRoute>
              } 
            />
            
            {/* Smart Recruiter Dashboard - Admin only */}
            <Route 
              path="/admin/recruiting" 
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <SmartRecruiterDashboard />
                </ProtectedRoute>
              } 
            />
            
            {/* Mobile Driver Companion - Dedicated /mobile route */}
            <Route 
              path="/mobile" 
              element={
                <ProtectedRoute allowedRoles={['driver', 'admin', 'master_admin', 'bus_company', 'guest_tour']}>
                  <MobileDriverView />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/mobile/driver" 
              element={
                <ProtectedRoute allowedRoles={['driver', 'admin', 'master_admin', 'bus_company', 'guest_tour']}>
                  <MobileDriverView />
                </ProtectedRoute>
              } 
            />
            
            <Route path="/advertise" element={<AdvertiserPortal />} />
            <Route path="/terms" element={<TermsOfUse />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            
            {/* Green Room — accessible to all authenticated users */}
            <Route
              path="/green-room"
              element={
                <ProtectedRoute>
                  <GreenRoomPage />
                </ProtectedRoute>
              }
            />
            
            <Route path="/unauthorized" element={<UnauthorizedPage />} />
              </Routes>
              </Suspense>
            </BrowserRouter>
          </div>
        </PWAAppWrapper>
      </PWAProvider>
    </FeatureFlagProvider>
      </PermissionsProvider>
  </AuthProvider>
  </ThemeProvider>
  );
}

// PWA App Wrapper Component
const PWAAppWrapper = ({ children }) => {
  const { user, loading } = useAuth();
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  const [locationGranted, setLocationGranted] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);

  // Check location permission when user is logged in
  useEffect(() => {
    if (user && !loading) {
      // Check if location permission is already granted
      if (navigator.permissions) {
        navigator.permissions.query({ name: 'geolocation' }).then(result => {
          if (result.state === 'granted') {
            setLocationGranted(true);
            setShowLocationModal(false);
          } else if (result.state === 'denied') {
            setShowLocationModal(true);
          } else {
            // Prompt state - show modal on first interaction or after delay
            // For now, allow access but can be made stricter
            setLocationGranted(true);
          }
        }).catch(() => {
          // Permissions API not supported, allow access
          setLocationGranted(true);
        });
      } else {
        // No Permissions API, allow access
        setLocationGranted(true);
      }
    }
  }, [user, loading]);

  const handleLocationGranted = () => {
    console.log('📍 Location granted - closing modal');
    setLocationGranted(true);
    setShowLocationModal(false);
  };

  return (
    <PWAGuard>
      {children}
      {/* Location Permission Modal - shows when denied - MANDATORY */}
      {showLocationModal && user && (
        <LocationPermissionModal onLocationGranted={handleLocationGranted} />
      )}
      {/* Onboarding modals removed from auto-popup - push notifications now in Settings */}
      {/* Floating AI Chatbot & Support - Available to all logged-in users */}
      {!loading && user && token && user.role && (
        <Chatbot 
          token={token}
          userRole={user.role}
          userName={user.full_name || user.email}
          userId={user.id}
        />
      )}
      {/* Legal acknowledgment modal — fires when a new privacy policy version is published */}
      {!loading && user && token && (
        <LegalAcknowledgmentModal />
      )}
    </PWAGuard>
  );
};

// Unauthorized Page Component
const UnauthorizedPage = () => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-950">
      <div className="max-w-md w-full bg-gray-900 border border-gray-700 rounded-2xl p-8 text-center">
        <div className="w-14 h-14 rounded-full bg-rose-500/10 border border-rose-500/30 flex items-center justify-center mx-auto mb-5">
          <svg className="h-7 w-7 text-rose-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /></svg>
        </div>
        <h1 className="text-2xl font-bold text-white mb-2">Access Denied</h1>
        <p className="text-gray-400 mb-6">You don't have permission to access this page.</p>
        <button
          onClick={() => window.history.back()}
          className="px-6 py-2.5 rounded-xl text-white font-semibold transition-all"
          style={{ background: 'linear-gradient(135deg, #f97316, #ea580c)' }}
        >
          Go Back
        </button>
      </div>
    </div>
  );
};

export default App;
